// LogoSpinner: the instance's mark turning while a whole page loads, the app's classic. Anything
// smaller than a page waits on Skeleton instead, shaped like what is coming. It fades in after a
// beat, so a quick load never flashes it, and under reduced motion it breathes instead of
// turning. docs/operations/design-standard.md, "Feedback".

import React, { useEffect, useState } from 'react';
import { instance } from '../../instance';

const APPEAR_AFTER_MS = 150;

export interface LogoSpinnerProps {
  /** What is loading, read to a screen reader ("Loading the fleet"). */
  label?: string;
  /**
   * `page` (default) centres the mark in the space a page would fill, as its live status.
   * `inline` is a 16px mark for a key whose action is running. The host button must set
   * `aria-busy` and keep its words, which carry the state; pass `label` only when it does not.
   */
  size?: 'page' | 'inline';
  className?: string;
}

// `ease` on the turn is the old spinner's own curve, not a stand-in for a motion token: each
// turn gathers and settles, which is the classic people know.
const TURN = 'motion-safe:animate-[spin_2s_ease_infinite] motion-reduce:animate-pulse';

export const LogoSpinner: React.FC<LogoSpinnerProps> = ({ label, size = 'page', className = '' }) => {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (size !== 'page') return undefined;
    const t = window.setTimeout(() => setShown(true), APPEAR_AFTER_MS);
    return () => window.clearTimeout(t);
  }, [size]);

  if (size === 'inline') {
    return (
      <>
        <img src={instance.loadingIcon} alt="" aria-hidden="true" className={`inline-block h-4 w-4 flex-none ${TURN} ${className}`} />
        {label && <span className="sr-only">{label}</span>}
      </>
    );
  }

  return (
    <div role="status" className={`flex min-h-[60dvh] w-full items-center justify-center ${className}`}>
      <img
        src={instance.loadingIcon}
        alt=""
        className={`loading-icon h-9 w-9 transition-opacity duration-standard ease-emphasized ${shown ? `opacity-100 ${TURN}` : 'opacity-0'}`}
      />
      <span className="sr-only">{label ?? 'Loading'}</span>
    </div>
  );
};

export default LogoSpinner;
